import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, CheckCircle2, Check } from "lucide-react";
import { Field, Text, Select } from "./FormFields";
import { ConsentCheckboxes } from "./ConsentCheckboxes";
import { useFormErrors } from "../hooks/useFormErrors";
import { validateBooking } from "../lib/formValidation";
import { submitInquiry } from "../lib/api";
import { fireConversion, CONVERSIONS } from "../lib/gtag";
import { PASSENGER_OPTIONS, PORTS } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";
import { localizePassengers } from "../i18n/content_it";
import { LegalDisclaimer } from "./LegalDisclaimer";

const reveal = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.35 },
};

export default function BookingForm({ tour, onClose }) {
  const t = useT();
  const { lang } = useLang();
  const passengerOptions = localizePassengers(PASSENGER_OPTIONS, lang, t("form.paxOnRequest"));
  const [form, setForm] = useState({
    full_name: "", email: "", confirm_email: "", phone: "", service_date: "", pickup_time: "", passengers: "",
  });
  const [pickupType, setPickupType] = useState("");
  const [d, setD] = useState({});
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeCancellation, setAgreeCancellation] = useState(false);
  const { errors, setErrors, clearErr } = useFormErrors();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); clearErr(k); };
  const setDetail = (k) => (e) => { setD((p) => ({ ...p, [k]: e.target.value })); clearErr(k); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateBooking({ form, pickupType, d, agreePrivacy, agreeCancellation });
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (form.email && form.confirm_email && form.email !== form.confirm_email)
        toast.error(t("form.toastEmailMismatch"));
      else
        toast.error(t("form.toastRequired"));
      return;
    }
    setErrors({});
    const details = { pickup_point: pickupType === "cruise" ? "Cruise ship" : "Hotel / apartment", ...d };
    setLoading(true);
    try {
      await submitInquiry({ kind: "booking", tour: tour.title, ...form, details });
      setSubmitted(true);
      fireConversion(CONVERSIONS.contactForm);
    } catch {
      toast.error(t("form.toastError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="booking-modal"
    >
      <motion.div
        className="bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 sm:p-8 md:p-12 relative"
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <button data-testid="booking-close" onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-[#C5A059] transition-colors">
          <X size={22} />
        </button>
        {submitted ? (
          <div data-testid="booking-success" className="text-center py-6">
            <CheckCircle2 size={48} className="text-[#C5A059] mx-auto" strokeWidth={1.2} />
            <h3 className="font-serif text-3xl md:text-4xl mt-6 mb-4">{t("form.successTitle")}</h3>
            <p className="text-white/70 max-w-md mx-auto leading-relaxed">
              {t("form.successText")}
            </p>
            <button
              data-testid="booking-success-close"
              onClick={onClose}
              className="mt-8 bg-[#C5A059] text-[#0A0A0A] px-8 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#D4AF37] transition-colors"
            >
              {t("form.close")}
            </button>
          </div>
        ) : (
        <>
        <p className="overline mb-2">{t("form.bookOverline")}</p>
        <h3 className="font-serif text-3xl md:text-4xl text-[#F5F5F0] mb-4 pr-8">{tour.title}</h3>
        {tour.highlights && (
          <div data-testid="booking-highlights" className="flex flex-wrap gap-x-5 gap-y-2 mb-8 pb-8 border-b border-white/10">
            {tour.highlights.map((h) => (
              <span key={h} className="inline-flex items-center gap-2 text-sm text-white/70">
                <Check size={14} className="text-[#C5A059] shrink-0" strokeWidth={2.2} />
                {h}
              </span>
            ))}
          </div>
        )}

        <form onSubmit={submit} noValidate className="space-y-7" data-testid="booking-form">
          <LegalDisclaimer className="pb-2" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            <Field label={t("form.fullName")} testid="bk-field-name" error={errors.full_name}>
              <Text testid="bk-name" error={errors.full_name} value={form.full_name} onChange={set("full_name")} placeholder={t("form.namePh")} />
            </Field>
            <Field label={t("form.phone")} testid="bk-field-phone" error={errors.phone}>
              <Text testid="bk-phone" error={errors.phone} value={form.phone} onChange={set("phone")} placeholder="+.." />
            </Field>
            <Field label={t("form.email")} testid="bk-field-email" error={errors.email}>
              <Text testid="bk-email" error={errors.email} type="email" value={form.email} onChange={set("email")} placeholder={t("form.emailPh")} />
            </Field>
            <Field label={t("form.confirmEmail")} testid="bk-field-confirm-email" error={errors.confirm_email}>
              <Text testid="bk-confirm-email" error={errors.confirm_email} type="email" value={form.confirm_email} onChange={set("confirm_email")} placeholder={t("form.confirmEmailPh")} onPaste={(e) => e.preventDefault()} />
            </Field>
            <Field label={t("form.passengers")} testid="bk-field-pax" error={errors.passengers}>
              <Select testid="bk-pax" error={errors.passengers} options={passengerOptions} value={form.passengers} onChange={set("passengers")} placeholder={t("form.passengersPhShort")} />
            </Field>
            <Field label={t("form.dateShort")} testid="bk-field-date" error={errors.service_date}>
              <Text testid="bk-date" error={errors.service_date} type="date" value={form.service_date} onChange={set("service_date")} />
            </Field>
            <Field label={t("form.pickupTime")} testid="bk-field-time" error={errors.pickup_time}>
              <Text testid="bk-time" error={errors.pickup_time} type="time" value={form.pickup_time} onChange={set("pickup_time")} />
            </Field>
            <Field label={t("form.pickupPoint")} testid="bk-field-pickup" error={errors.pickup_type}>
              <Select testid="bk-pickup-type" error={errors.pickup_type} options={[{ value: "cruise", label: t("form.optCruise") }, { value: "hotel", label: t("form.optHotel") }]} value={pickupType} onChange={(e) => { setPickupType(e.target.value); setD({}); clearErr("pickup_type"); }} placeholder={t("form.pickupPointPh")} />
            </Field>
          </div>

          <AnimatePresence mode="wait">
            {pickupType === "cruise" && (
              <motion.div key="c" {...reveal} className="overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7 pt-1">
                  <Field label={t("form.shipName")} testid="bk-field-ship" error={errors.ship_name}>
                    <Text testid="bk-ship" error={errors.ship_name} value={d.ship_name || ""} onChange={setDetail("ship_name")} placeholder={t("form.shipNamePh")} />
                  </Field>
                  <Field label={t("form.dockingPort")} testid="bk-field-port" error={errors.port}>
                    <Select testid="bk-port" error={errors.port} options={PORTS.cruise} value={d.port || ""} onChange={setDetail("port")} placeholder={t("form.portPh")} />
                  </Field>
                </div>
              </motion.div>
            )}
            {pickupType === "hotel" && (
              <motion.div key="h" {...reveal} className="overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7 pt-1">
                  <Field label={t("form.address")} testid="bk-field-address" error={errors.address}>
                    <Text testid="bk-address" error={errors.address} value={d.address || ""} onChange={setDetail("address")} placeholder={t("form.addressPh")} />
                  </Field>
                  <Field label={t("form.city")} testid="bk-field-city" error={errors.city}>
                    <Text testid="bk-city" error={errors.city} value={d.city || ""} onChange={setDetail("city")} placeholder={t("form.cityPh")} />
                  </Field>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <ConsentCheckboxes
            agreePrivacy={agreePrivacy}
            setAgreePrivacy={setAgreePrivacy}
            agreeCancellation={agreeCancellation}
            setAgreeCancellation={setAgreeCancellation}
            errors={errors}
            clearErr={clearErr}
            privacyTestId="bk-agree-privacy"
            cancellationTestId="bk-agree-cancellation"
          />

          <button
            type="submit"
            data-testid="booking-submit"
            disabled={loading}
            className="w-full bg-[#C5A059] text-[#0A0A0A] py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37] transition-colors disabled:opacity-60"
          >
            {loading ? t("form.sending") : t("form.requestBooking")}
          </button>
        </form>
        </>
        )}
      </motion.div>
    </motion.div>
  );
}
